import {
	APNSCertificateType,
	InputType,
	ServiceStatus,
	TBrandingOptions,
} from "./common";

export enum FormMode {
	List = "List",
	Edit = "Edit",
	New = "New",
}

export interface IFileInfo {
	fileName?: string;
	signedUrl?: string;
}

export interface IPhoneNumber {
	countryCode: string;
	number: string;
}

export interface IRegionalAttribute {
	name: string;
	value?: string;
	type: InputType;
	title: string;
}

export interface IBusinessDetailsModel {
	fields: IRegionalAttribute[];
}

export interface INewNumberModel {
	number: string;
	ownershipDocuments: IFileInfo[];
	status: ServiceStatus;
}

export interface INewBusinessModel {
	primaryContact: IContactModel;
	details?: IBusinessDetailsModel;
	info?: IBusinessInfoModel;
	numbers: INewNumberModel[];
	countryCode: string;
}

export interface IBusinessModel {
	businessId: string;
	businessCode: string;
	name: string;
	logo: IFileInfo;
	status?: ServiceStatus;
}

export interface IBusinessListModel {
	businessId: string;
	businessCode: string;
	name: string;
	status: ServiceStatus;
	trialEnabled?: boolean;
	logo: IFileInfo;
}

export interface IBusinessInfoModel {
	businessId: string;
	businessCode: string;
	name: string;
	website: string;
	dunsNumber: string;
	industry: string;
	status: ServiceStatus;
	countryCode: string;
	logo?: IFileInfo;
	trialEnabled?: boolean;
}

export interface IBusinessNumberApprovalModel {
	businessNumber: string
	status: ServiceStatus;
}

export interface IBusinessApprovalPrimaryContact {
	firstName: string;
	lastName: string;
	emailId: string;
	phoneNumber: string;
}

export interface IBusinessApprovalAttributeSection {
	sectionId: string;
	sectionName: string;
	attributes: IBusinessApprovalAttribute[];
}

export interface IBusinessApprovalAttributeValue {
	name: string;
	value: string;
}

export interface IBusinessApprovalAttribute {
	value: IBusinessApprovalAttributeValue;
	title: string;
}

export interface IBusinessApprovalApprovalApprovedBy {
	emailId: string;
}

export interface IBusinessApprovalApprovalDetails {
	approvedAt: number;
	approvedBy: IBusinessApprovalApprovalApprovedBy;
	cPaaSProviderConfig: VonageApplicationConfig;
	comments: string;
	supportEmailAddress: string;
}

export interface IBusinessApprovalModel {
	businessId: string;
	businessCode: string;
	name: string;
	website: string;
	dunsNumber: string;
	industry: string;
	status: ServiceStatus;
	countryCode: string;
	logo?: IFileInfo;
	trialEnabled?: boolean;
	businessNumbers: IBusinessNumberApprovalModel[]
	primaryContact: IBusinessApprovalPrimaryContact;
	attributeSections: IBusinessApprovalAttributeSection[];
	approvalDetails: IBusinessApprovalApprovalDetails;
}


export interface IMobileAppUpdateModel {
	brandingAppCode: string;
	code: string;
	name: string;
	businessId?: string;
	iOSApp: {
		action: TRequestAction;
		iOSAppSettings?: IOSAppSettings
	}
	androidApp: {
		action: TRequestAction
		androidAppSettings?: AndroidAppSettings
	}
}

export interface IMobileAppModel {
	id: string;
	code: string;
	name: string;
	businessId?: string;
	iOSAppSettings?: IOSAppSettings;
	androidAppSettings?: AndroidAppSettings;
	bundleId: string;
	packageName?: string;
}

export interface IOSAppSettings {
	bundleId: string;
	certificateType: APNSCertificateType;
	certificateFile: IFileInfo;
	p8FileSettings?: IOSAppP8FileSettings;
	p12FileSettings?: IOSAppP12FileSettings;
}

export interface IOSAppP8FileSettings {
	applePlatformTeamID: string;
	applePlatformKeyID: string;
	applePlatformBundleID: string;
}

export interface IOSAppP12FileSettings {
	p12Password?: string;
}

export interface AndroidAppSettings {
	packageName: string;
	certificateFile: IFileInfo;
}

export interface DeviceCredentialModel {
	iosFileUrl: string;
	androidFileUrl: string;
	secret: string;
}

export type TBrandingAppOption = 'SecuredCallsApp' | 'ExternalApp'

export interface IBrandingAppModel {
	option: TBrandingAppOption;
	brandingAppCode?: string;
	brandingAppId?: string;
	brandingAppName?: string;
}

export type TContactCenterOption = 'ExternalContactCentre' | 'None';

export interface IContactCenterModel {
	option: TContactCenterOption;
	contactCentreId?: string;
}

export interface INewApplicationModel {
	name: string;
	description: string;
	countryCode: string; // Country code where the business is registered
	businessId: string; // ID of the business
	brandingApp: IBrandingAppModel;
	contactCentre?: IContactCenterModel;
	brandingOption: TBrandingOptions;
	managedApplicationSettings?: IManagedApplicationSettings
	businessNumbers: INewBusinessNumberModel[];
	numberProviderConfig?: VonageApplicationConfig;
	scProvidedNumbers: boolean;
}

export interface IManagedApplicationSettings {
	cPaaSProvider: string;
	inboundCall?: IManagedApplicationInboundSettings;
	outBoundCall: IManagedApplicationOutboundSettings;
}

export interface IManagedApplicationInboundSettings {
	routingOption: TCallRoutingOption;
	sipDomain?: string;
	phoneNumber?: string;
	announcementText?: string;
}

export interface IManagedApplicationOutboundSettings {
	routingOption: TCallRoutingOption;
	fallBackEvents: TOutboundCallFallBackEvent[];
	announcementText?: string;
	alwaysUseDefaultIntent: boolean;
	timeout?: number;
}

export interface IApplicationModel {
	applicationId: string;
	name: string;
	description: string;
	countryCode: string; // Country code where the business is registered
	businessId: string; // ID of the business
	business: IBusinessModel;
	brandingAppCode: string;
	contactCentre: { id: string; code: string; name: string };
	brandingApplication: {
		id: string;
		code: string;
		name: string;
	};
	managedApplicationSettings?: IManagedApplicationSettings
	brandingOption: TBrandingOptions;
	numbers: IBusinessNumberModel[];
	scProvidedNumbers: boolean;
	screenSettings: ICallScreenSettings;
	status: string;
}

export interface ApplicationListModel {
	applicationId: string;
	brandingApplication: {
		id: string;
		code: string;
		name: string;
	};
	contactCentre: { id: string; code: string; name: string };
	business: { id: string; code: string; name: string };
	brandingOption: TBrandingOptions;
	status: string;
}

export interface IAddNumbersToApplicationRequestModel {
	businessNumbers: INewBusinessNumberModel[]
}

export interface INewBusinessNumberModel {
	businessNumber: string;
	branding: IBrandingSettings;
	managedNumberSettings?: IManagedNumberSettings;
}

export interface IBrandingSettings {
	defaultTTLInMinutes: number;
	brandingName: string;
	defaultIntent: string;
	imageFile: IFileInfo;
	setupPushContent: IAlertContent;
	cleanupPushContent: IAlertContent;
}

export interface IBusinessNumberUpdateModel {
	businessNumber: string;
	brandingName: string;
	defaultIntent: string;
	defaultTTLInMinutes: number;
	imageFile?: IFileInfo;
	setupPushContent: IAlertContent;
	cleanupPushContent: IAlertContent;
}

export interface VonageApplicationConfig {
	applicationId: string;
	privateKeyFile: IFileInfo;
	apiSignatureSecret?: string;
}

export interface IAlertContent {
	title: string;
	body: string;
}

export interface IBusinessNumberModel {
	businessNumber: string;
	businessId: string;
	status: ServiceStatus;
	branding: IBrandingSettings;
	managedNumberSettings: IManagedNumberSettings;
	applicationBusinessId?: string;
	scProvidedNumber: boolean;
	selected: boolean;
	hasUnsavedChanges: boolean;
	applicationBundleCode: string;
}

export type BusinessNumberEditModel = IBusinessNumberModel & {
	isModified: boolean;
};

export interface IManagedNumberSettings {
	cPaaSProvider: string;
	screenSettings?: ICallScreenSettings;
}

export type TCallRoutingOption = "PSTN" | "WEBRTC" | "SIP";
export type TOutboundCallFallBackEvent = "timeout" | "failed";

export interface IOutgoingCallSettings {
	routingOption: TCallRoutingOption;
	fallBackEvents?: string[];
	announcementText?: string;
	alwaysUseDefaultIntent: boolean;
}

export interface IIncomingCallSettings {
	sipEndpointUrl?: string;
	announcementText?: string;
}

export interface ICallScreenSettings {
	otherButton: OtherButton;
	endCallButton: OtherButton;
	appLogo: AppLogo;
	background: Background;
	answerCallButton: OtherButton;
}

export interface Background {
	color: string;
}

export interface AppLogo {
	size: Size;
}

export interface Size {
	width: number;
	height: number;
}

export interface OtherButton {
	color: Color;
}

export interface Color {
	background: string;
	foreground: string;
}

export interface IPhoneNumber {
	countryCode: string;
	number: string;
}

export interface IContactModel {
	userId?: string
	firstName: string;
	lastName: string;
	emailId: string;
	phoneNumber?: IPhoneNumber;
}

export type TContactCenterIntegrationOption = 'WEBSOCKET' | 'WEBHOOK' | 'NONE';
export type TContactCenterIntegrationWebHookEvents = 'BRANDING_STATUS_UPDATED' | 'CONNECTION_ACK' | 'CLEAR_BRANDING_STATUS_UPDATED' | 'BRANDING_ERROR';

export interface IContactCenterIntegrationWebhookConfig {
	httpMethod: string;
	webhookUrl: string;
	retryPolicy: { maxRetries: 3, retryIntervalInSeconds: 10 };
	authMethod: string
}

export interface IContactCentreModel {
	contactCentreId: string;
	contactCentreCode: string;
	productName: string;
	name: string;
	status: ServiceStatus;
	businessId?: string;
	option: TContactCenterOption
	allowedUrls: string[];
	eventDeliveryMethod: TContactCenterIntegrationOption;
	enabledEvents: TContactCenterIntegrationWebHookEvents[];
	webhookConfig?: IContactCenterIntegrationWebhookConfig
}

export interface TrialUsersModel {
	businessId: string;
	emailId: string;
	brandedNumber: string;
}

export { APNSCertificateType };

export type {
	InputType,
	ServiceStatus,
	TBrandingOptions,
};

export type TRequestAction = 'Update' | 'Add' | 'Remove';

export interface IUpdateApplicationRequest {
	name: string;
	description?: string;
	managedApplicationSettings?: IManagedApplicationSettings;
	businessNumbers: IApplicationNumberUpdateNumbers[];
}

export interface IApplicationNumberUpdateNumbers {
	number: IApplicationNumber | IApplicationNumberUpdate | IApplicationNumberRemove;
	action: TRequestAction;
}

export interface IApplicationNumber {
	businessNumber: string;
	branding: IBrandingSettings;
	managedNumberSettings?: IManagedNumberSettings;
}

export interface IApplicationNumberUpdate {
	businessNumber: string;
	branding: IBrandingSettings;
	managedNumberSettings?: IManagedNumberSettings;
}

export interface IApplicationNumberRemove { businessNumber: string; }

export interface IDeleteBusinessNumberRequest {
	businessNumbers: string[];
}

export interface IUserScope {
	id: string;
	name: string;
	key: string;
}